import { TaskDetail } from "./tasks.types";
import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
} from "@tanstack/react-query";
import { useFetch } from "@/lib/axios.config.ts";
import { PaginationResponse } from "@/shared/types/api.types.ts";
import { TaskCollection, TaskList } from "@/pages/get-don/tasks.types.ts";
import { useTranslation } from "react-i18next";
import showCustomToast from "@/shared/utils/showCustomToast.ts";
import { appConfig } from "@/lib/site-config";
import {
  getNextPageParam,
  getPreviousPageParam,
  initialPageParam,
} from "@/shared/utils/query";

export enum TaskKeys {
  Collection = "taskCollection",
  TaskList = "taskList",
  TaskCollectoinDetail = "taskCollectionDetail",
  TaskDetail = "taskDetail",
  CategoryList = "taskCategoryList",
}

export const useTaskCollectionListOptions = (
  params: { special?: boolean | null; weekly?: boolean | null },
  selectedCategoryId?: string
) => {
  return infiniteQueryOptions({
    queryKey: [TaskKeys.Collection, params],
    queryFn: async () => {
      const res = await useFetch.get<PaginationResponse<TaskCollection>>(
        "/tasks/TaskCollectionList",
        {
          params: {
            ...params,
            limit: appConfig.infiniteQueryLimit,
            category_id: selectedCategoryId ?? undefined,
          },
        }
      );
      return res.data;
    },
    initialPageParam,
    getNextPageParam: getNextPageParam,
    getPreviousPageParam: getPreviousPageParam,
  });
};

export const useTaskCollectionList = (selectedCategoryId?: string) => {
  return infiniteQueryOptions({
    queryKey: [TaskKeys.Collection, selectedCategoryId],
    queryFn: async () => {
      const res = await useFetch.get<PaginationResponse<TaskCollection>>(
        "/tasks/TaskCollectionList",
        {
          params: {
            limit: appConfig.infiniteQueryLimit,
            category_id: selectedCategoryId ?? undefined,
          },
        }
      );
      return res.data;
    },
    initialPageParam,
    enabled: !!selectedCategoryId,
    getNextPageParam: getNextPageParam,
    getPreviousPageParam: getPreviousPageParam,
  });
};

type TaskListPayload = {
  selectedCategory: string | null;
  inList?: boolean;
  enabled?: boolean;
};

export const useTaskList = (props: TaskListPayload) => {
  const { inList, selectedCategory, enabled = true } = props;
  return infiniteQueryOptions({
    enabled: !!enabled,
    queryKey: [TaskKeys.TaskList, selectedCategory],
    queryFn: async ({ pageParam }) => {
      const res = await useFetch.get<PaginationResponse<TaskList>>(
        "/tasks/TaskList",
        {
          params: {
            show_in_list: inList,
            limit: appConfig.infiniteQueryLimit,
            category_id: selectedCategory ?? undefined,
            offset: pageParam,
          },
        }
      );
      return {
        ...res.data,
        // results: res.data.results.map((task) => ({
        //   ...task,
        //   user_process: {
        //     ...task.user_process,
        //     cur_stage_value: 10000,
        //   },
        // })),
      };
    },
    initialPageParam,
    getNextPageParam: getNextPageParam,
    getPreviousPageParam: getPreviousPageParam,
    refetchOnWindowFocus: false,
  });
};

export const useTaskDetailOptions = (taskId?: string) => {
  return queryOptions({
    queryKey: [TaskKeys.TaskDetail, taskId],
    queryFn: async () => {
      const res = await useFetch.get<PaginationResponse<TaskList>>(
        "/tasks/TaskList",
        {
          params: {
            task_id: taskId,
          },
        }
      );
      return res.data.results[0];
    },
    enabled: !!taskId,
    refetchOnWindowFocus: false,
  });
};

export const useTaskCollectionDetailOptions = (taskId?: string) => {
  return queryOptions({
    queryKey: [TaskKeys.TaskCollectoinDetail, taskId],
    queryFn: async () => {
      const res = await useFetch.get<TaskDetail>(
        `/tasks/TaskCollectionDetail/${taskId}`
      );
      return res.data;
    },
    enabled: !!taskId,
    refetchOnWindowFocus: false,
  });
};

export const useTaskCategoryListOptions = () => {
  return queryOptions({
    queryKey: [TaskKeys.CategoryList],
    queryFn: async () => {
      const res = await useFetch.get<
        { id: string; name: string; is_collection_category: boolean }[]
      >("/tasks/TaskCategoryList");
      return res.data;
    },
  });
};

export const useCollectionAwardMutation = () => {
  const { t } = useTranslation();
  return useMutation({
    mutationKey: ["get-collection-award"],
    mutationFn: async (collectionId: string) => {
      const res = await useFetch.post<string>(
        `tasks/TaskCollectionComplete/${collectionId}`
      );
      return res.data;
    },
    onError: () => {
      showCustomToast(t("get_award_failed"), "error");
    },
  });
};
